import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { LogOut, User, Shield } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import logo from '@/assets/logo.png';
export const NavBar = () => {
  const {
    user,
    signOut
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Landing pages that should show marketing navbar when not logged in
  const landingPages = ['/', '/docs', '/contact', '/buy'];
  const isLandingPage = landingPages.includes(location.pathname);

  // Check if user has admin role
  const {
    data: isAdmin
  } = useQuery({
    queryKey: ['admin-role', user?.id],
    queryFn: async () => {
      if (!user?.id) return false;
      const {
        data
      } = await supabase.rpc('has_role', {
        _user_id: user.id,
        _role: 'admin'
      });
      return data || false;
    },
    enabled: !!user?.id
  });
  // Marketing-style navbar for landing page
  if (isLandingPage && !user) {
    return <nav className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/10" role="navigation" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" aria-label="NowRamp Home">
            <img src={logo} alt="NowRamp logo" width={44} height={32} className="h-8 w-auto" />
            <span className="text-xl font-bold">
              <span className="text-white">Now</span><span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Ramp</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center bg-white/5 border border-white/10 rounded-full p-1" role="menubar">
            <button 
              onClick={() => navigate('/')} 
              className={`px-4 py-2 rounded-full text-sm transition-colors focus:outline-none ${location.pathname === '/' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}
              role="menuitem"
            >
              Home
            </button>
            <a 
              href="https://docs.nowramp.com/" 
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-full text-sm transition-colors focus:outline-none text-slate-400 hover:text-white"
              role="menuitem"
            >
              Documentation
            </a>
            <button 
              onClick={() => navigate('/buy')} 
              className={`px-4 py-2 rounded-full text-sm transition-colors focus:outline-none ${location.pathname === '/buy' ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-white'}`}
              role="menuitem"
            >
              Buy & Sell
            </button>
          </div>

          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/auth')} className="text-sm text-slate-300 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-2 py-1">
              Sign In
            </button>
            <button onClick={() => navigate('/buy')} className="px-5 py-2 bg-white text-black text-sm font-semibold rounded-full hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50">
              Buy Now
            </button>
          </div>
        </div>
      </nav>;
  }

  // Authenticated/app navbar
  return <nav className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50" role="navigation" aria-label="Main navigation">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" aria-label="NowRamp Home">
          <img src={logo} alt="NowRamp logo" width={44} height={32} className="h-8 w-auto" />
          <span className="text-xl font-bold">
            <span className="text-white">Now</span><span className="bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">Ramp</span>
          </span>
        </Link>

        <div className="flex items-center gap-4">
          {user ? <>
              {isAdmin && <Button variant="ghost" onClick={() => navigate('/admin')} className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Admin
                </Button>}
              <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
              <Button onClick={() => navigate('/buy')} className="bg-gradient-to-r from-primary to-secondary hover:opacity-90">
                Buy
              </Button>
              <Button variant="ghost" onClick={() => navigate('/kyc')}>
                KYC
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon">
                    <User className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {isAdmin && <>
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <Shield className="mr-2 h-4 w-4" />
                        Admin Panel
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>}
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/buy')}>
                    Buy Crypto
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/kyc')}>
                    KYC Verification
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={signOut} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </> : <>
              <Button variant="ghost" onClick={() => navigate('/auth')}>Get Started</Button>
              <Button onClick={() => navigate('/auth')}>Login</Button>
            </>}
        </div>
      </div>
    </nav>;
};